/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2024 Prodeo - All Rights Reserved
 */

import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService } from '@umanforge/common';
import { AxiosResponse, AxiosError } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';
import { API } from 'constants/api.constant';
import { DiaryNote, DiaryNoteFilter, DiaryNoteResponse } from '../interfaces/diaryNote.interface';
import { GlobalService } from './global.service';

/* ===== Services ===== */
export default class DiaryNoteService {

  /* ===== Send request to create a diary note ===== */
  static createDiaryNote(
    diaryNote: { noteId: string, siteId: string },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<DiaryNote> {
    const body = {
      noteId: diaryNote.noteId,
      siteId: diaryNote.siteId,
    };
    return new Promise( async resolve => {
      AXIOS.post( `${API.DIARY_NOTE}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<DiaryNote> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to delete a diary note by ID ===== */
  static deleteDiaryNoteById(
    diaryNoteId: string,
    siteId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<void> {
    const body = { siteId };
    return new Promise( async resolve => {
      AXIOS.delete(
        `${API.DIARY_NOTE}/${diaryNoteId}`,
        {
          ...await CommonGlobalService.getHeaderRequest( token ),
          data: body
        }
      )
        .then( ( response: AxiosResponse<void> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve();
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to get diary notes by site ===== */
  static getDiaryNotesByFilter(
    filter: DiaryNoteFilter,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ total: number, datas: DiaryNoteResponse[] }> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.DIARY_NOTE}/by-filter`, { ...await CommonGlobalService.getHeaderRequest( token ), params: filter } )
        .then( ( response: AxiosResponse<{ total: number, datas: DiaryNoteResponse[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }
}